import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectCrossClientResultsExecution } from '../../../../store/crossClient/crossClientResults'
import { triggerCrossClientActionsPolling } from '../../../../store/crossClient/crossClientActions'
import { useUpdateCrossClientIntegrationEntitiesMutation } from '../../../../store/crossClient/crossClient'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { IntegrationServiceId } from '../../../../common/integrations'

import { type WealthboxEligibility } from './useWealthboxEligibility'

interface ToastConfig {
  title: string
  message: string
}

interface UseWealthboxActionSaveOptions {
  eligibility: WealthboxEligibility
  onClose: () => void
  sourceObject: string
  entityType: string
  relatedToType: string
  successToast: ToastConfig
  errorToast: ToastConfig
}

interface UseWealthboxActionSaveResult {
  save: (entity: Record<string, unknown>) => Promise<void>
  saving: boolean
}

export const useWealthboxActionSave = ({
  eligibility,
  onClose,
  sourceObject,
  entityType,
  relatedToType,
  successToast,
  errorToast,
}: UseWealthboxActionSaveOptions): UseWealthboxActionSaveResult => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)

  const [updateIntegrationEntities, { isLoading: saving }] = useUpdateCrossClientIntegrationEntitiesMutation()

  const save = async (entity: Record<string, unknown>) => {
    if (eligibility.status !== 'ready' || !execution?.id || !accountId) {
      return
    }

    try {
      await updateIntegrationEntities({
        accountId,
        executionId: execution.id,
        connector: IntegrationServiceId.WEALTHBOX,
        relatedToType,
        sourcePlatform: 'zocks',
        sourceObject,
        entityType,
        entity,
        targets: eligibility.eligibleTargets,
      }).unwrap()

      dispatch(triggerCrossClientActionsPolling(Date.now()))
      dispatch(addToast({ variant: 'success', title: successToast.title, messages: [successToast.message] }))
      onClose()
    } catch {
      dispatch(addToast({ variant: 'error', title: errorToast.title, messages: [errorToast.message] }))
    }
  }

  return { save, saving }
}
